import { createFileRoute, Outlet, useNavigate, useLocation } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useState, useEffect, useCallback } from 'react';
import { track } from '@/lib/analytics';
import { FiMessageCircle } from 'react-icons/fi';
import { apiJson } from '@/lib/api';
import { useHouseholds } from '@/lib/household-context';
import type { ConversationsResponse, SelectedConversation, UserResponse } from '@/lib/types';
import {
  ChatSearchInput,
  ChatSearchResults,
  CHAT_SEARCH_MIN_LENGTH,
  ChatView,
  ConversationsList,
} from '@/components';

// Hook to detect if we're on desktop
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isDesktop;
}

function ChatLayout() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useIsDesktop();

  // Track screen view
  useEffect(() => {
    track('Chat List Screen');
  }, []);

  // Selected conversation for desktop split view
  const [desktopSelected, setDesktopSelected] = useState<SelectedConversation | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [focusNonce, setFocusNonce] = useState(0);
  const isSearching = searchQuery.trim().length >= CHAT_SEARCH_MIN_LENGTH;

  // Fetch user (for custom-chat creator / navigation)
  const userQuery = useQuery({
    queryKey: ['user'],
    queryFn: () => apiJson<UserResponse>(getToken, '/v1/user'),
    enabled: isLoaded && !!isSignedIn,
  });
  const currentUserId = userQuery.data?.id;
  const { currentHouseholdId } = useHouseholds();

  // Fetch conversations
  const conversationsQuery = useQuery({
    queryKey: ['conversations', currentHouseholdId],
    queryFn: () => apiJson<ConversationsResponse>(getToken, '/v1/conversations'),
    enabled: isLoaded && !!isSignedIn,
    refetchInterval: 30000, // Poll every 30 seconds
  });

  // Determine what conversation is currently shown based on route
  const getCurrentConversation = useCallback((): SelectedConversation | null => {
    const path = location.pathname;
    
    // Check if we're on the main chat route
    if (path === '/app/chat/main') {
      return { type: 'main' };
    }

    const customMatch = path.match(/\/app\/chat\/custom\/(\d+)/);
    if (customMatch) {
      const chatId = parseInt(customMatch[1], 10);
      const c = conversationsQuery.data?.custom_chats.find(
        (x) => x.chat_room_id === chatId
      );
      return {
        type: 'custom-chat',
        chatId,
        title: c?.title ?? 'Chat',
        isArchived: c?.is_archived ?? false,
        isCreator: c ? userQuery.data?.id === c.creator_user_id : false,
      };
    }

    // Check for expense dispute route
    const expenseMatch = path.match(/\/app\/chat\/expense\/(\d+)/);
    if (expenseMatch) {
      const disputeId = parseInt(expenseMatch[1], 10);
      const dispute = conversationsQuery.data?.unresolved_disputes
        .concat(conversationsQuery.data?.resolved_disputes ?? [])
        .find((d) => d.dispute_type === 'expense' && d.dispute_id === disputeId);
      return {
        type: 'expense-dispute',
        disputeId,
        itemId: dispute?.item_id ?? 0,
      };
    }
    
    // Check for payment dispute route
    const paymentMatch = path.match(/\/app\/chat\/payment\/(\d+)/);
    if (paymentMatch) {
      const disputeId = parseInt(paymentMatch[1], 10);
      const dispute = conversationsQuery.data?.unresolved_disputes
        .concat(conversationsQuery.data?.resolved_disputes ?? [])
        .find((d) => d.dispute_type === 'payment' && d.dispute_id === disputeId);
      return {
        type: 'payment-dispute',
        disputeId,
        itemId: dispute?.item_id ?? 0,
      };
    }
    
    return null;
  }, [location.pathname, conversationsQuery.data, userQuery.data?.id]);

  // Helper to compare two conversations for equality
  const areConversationsEqual = useCallback(
    (a: SelectedConversation | null, b: SelectedConversation | null): boolean => {
      if (a === b) return true;
      if (!a || !b) return false;
      if (a.type !== b.type) return false;
      if (a.type === 'main') return true;
      if (a.type === 'expense-dispute' && b.type === 'expense-dispute') {
        return a.disputeId === b.disputeId;
      }
      if (a.type === 'payment-dispute' && b.type === 'payment-dispute') {
        return a.disputeId === b.disputeId;
      }
      if (a.type === 'custom-chat' && b.type === 'custom-chat') {
        return a.chatId === b.chatId;
      }
      return false;
    },
    []
  );

  // Sync desktop selected with route
  useEffect(() => {
    if (isDesktop) {
      const current = getCurrentConversation();
      if (current) {
        // Only update if the conversation actually changed
        if (!areConversationsEqual(current, desktopSelected)) {
          setDesktopSelected(current);
        }
      } else if (!desktopSelected) {
        // Default to main chat on desktop if no conversation is selected
        setDesktopSelected({ type: 'main' });
      }
    }
  }, [isDesktop, getCurrentConversation, desktopSelected, areConversationsEqual]);

  // Handle conversation selection
  const handleSelectConversation = useCallback(
    (conversation: SelectedConversation) => {
      if (isDesktop) {
        // On desktop, update selected state and optionally update URL
        setDesktopSelected(conversation);
        // Also update the URL for deep linking
        if (conversation.type === 'main') {
          navigate({ to: '/app/chat/main' });
        } else if (conversation.type === 'custom-chat') {
          navigate({
            to: '/app/chat/custom/$chatId',
            params: { chatId: String(conversation.chatId) },
          });
        } else if (conversation.type === 'expense-dispute') {
          navigate({ to: '/app/chat/expense/$disputeId', params: { disputeId: String(conversation.disputeId) } });
        } else {
          navigate({ to: '/app/chat/payment/$disputeId', params: { disputeId: String(conversation.disputeId) } });
        }
      } else {
        // On mobile, navigate to the conversation
        if (conversation.type === 'main') {
          navigate({ to: '/app/chat/main' });
        } else if (conversation.type === 'custom-chat') {
          navigate({
            to: '/app/chat/custom/$chatId',
            params: { chatId: String(conversation.chatId) },
          });
        } else if (conversation.type === 'expense-dispute') {
          navigate({ to: '/app/chat/expense/$disputeId', params: { disputeId: String(conversation.disputeId) } });
        } else {
          navigate({ to: '/app/chat/payment/$disputeId', params: { disputeId: String(conversation.disputeId) } });
        }
      }
    },
    [isDesktop, navigate]
  );

  const handleSelectSearchResult = useCallback(
    (conversation: SelectedConversation) => {
      setFocusNonce((nonce) => nonce + 1);
      handleSelectConversation(conversation);
    },
    [handleSelectConversation],
  );

  const handleNewCustomChatCreated = useCallback(
    (id: number) => {
      handleSelectConversation({
        type: 'custom-chat',
        chatId: id,
        title: '…',
        isArchived: false,
        isCreator: true,
      });
    },
    [handleSelectConversation]
  );

  // Get dispute info for selected conversation
  const getDisputeInfo = useCallback(
    (conv: SelectedConversation | null) => {
      if (!conv || conv.type === 'main' || conv.type === 'custom-chat')
        return undefined;
      
      const allDisputes = [
        ...(conversationsQuery.data?.unresolved_disputes ?? []),
        ...(conversationsQuery.data?.resolved_disputes ?? []),
      ];
      
      const dispute = allDisputes.find(
        (d) =>
          d.dispute_type === (conv.type === 'expense-dispute' ? 'expense' : 'payment') &&
          d.dispute_id === conv.disputeId
      );
      
      if (!dispute) return undefined;
      
      return {
        itemName: dispute.item_name,
        isResolved: dispute.is_resolved,
      };
    },
    [conversationsQuery.data]
  );

  // Check if we're on a child route (showing a specific conversation on mobile)
  const isOnChildRoute = location.pathname !== '/app/chat' && location.pathname !== '/app/chat/';
  const isIndexRoute = !isOnChildRoute;

  // On mobile, if we're on the index route, show the list
  // On desktop, always show split view
  if (!isDesktop) {
    // Mobile layout
    if (isIndexRoute) {
      // Show conversation list that navigates to main chat or disputes
      return (
        <div className='min-h-screen bg-sky-light flex flex-col'>
          {/* Header */}
          <header className='px-4 py-3 border-b border-gray-200 bg-sky-light'>
            <h1
              className='text-2xl font-light text-primary text-center'
              style={{
                fontFamily: "'Khula', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              Messages
            </h1>
            <ChatSearchInput value={searchQuery} onChange={setSearchQuery} />
          </header>

          {isSearching ? (
            <ChatSearchResults
              query={searchQuery.trim()}
              onSelect={handleSelectSearchResult}
            />
          ) : (
            <ConversationsList
              conversations={conversationsQuery.data}
              isLoading={conversationsQuery.isLoading}
              isError={conversationsQuery.isError}
              selected={null}
              onSelect={handleSelectConversation}
              currentUserId={currentUserId}
              onNavigateToNewCustomChat={handleNewCustomChatCreated}
            />
          )}
        </div>
      );
    }
    
    // On child route, render the outlet (main chat or dispute)
    return <Outlet />;
  }

  // Desktop layout - split view
  return (
    <div className='flex h-screen bg-sky-light'>
      {/* Conversation list sidebar */}
      <div className='w-80 border-r border-gray-200 flex flex-col bg-sky-light'>
        <header className='px-4 py-4 border-b border-gray-200'>
          <h1
            className='text-xl font-semibold text-gray-900'
            style={{
              fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            Messages
          </h1>
          <ChatSearchInput value={searchQuery} onChange={setSearchQuery} />
        </header>

        {isSearching ? (
          <ChatSearchResults
            query={searchQuery.trim()}
            onSelect={handleSelectSearchResult}
          />
        ) : (
          <ConversationsList
            conversations={conversationsQuery.data}
            isLoading={conversationsQuery.isLoading}
            isError={conversationsQuery.isError}
            selected={desktopSelected}
            onSelect={handleSelectConversation}
            currentUserId={currentUserId}
            onNavigateToNewCustomChat={handleNewCustomChatCreated}
          />
        )}
      </div>

      {/* Chat view */}
      <div className='flex min-h-0 flex-1 flex-col'>
        {desktopSelected ? (
          <ChatView
            conversation={desktopSelected}
            currentUser={userQuery.data}
            showHeader={true}
            focusNonce={focusNonce}
            disputeInfo={getDisputeInfo(desktopSelected)}
          />
        ) : (
          <div className='flex-1 flex items-center justify-center'>
            <div className='text-center'>
              <div className='w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4'>
                <FiMessageCircle className='w-8 h-8 text-gray-400' />
              </div>
              <p className='text-gray-500'>Select a conversation to start messaging</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/chat')({
  component: ChatLayout,
});
